import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Abstract3DShapes, DotPattern } from "@/components/decorative";
import { FloatingSwitchers } from "@/components/shared/FloatingSwitchers";
import { posthog } from "@/lib/posthog";
import { ArrowRight, Zap } from "lucide-react";

export const Hero = () => {
  const { t } = useTranslation();

  const scrollToForm = () => {
    const formElement = document.getElementById("waitlist-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }

    // GTM event
    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: "cta_click",
        cta_location: "hero",
      });
    }

    // PostHog event
    if (posthog.__loaded) {
      posthog.capture('cta_clicked', {
        location: 'hero',
        button_text: 'Join the Waitlist',
      });
    }
  };

  const scrollToQuiz = () => {
    window.location.href = '/quiz';
    
    if (posthog.__loaded) {
      posthog.capture('secondary_cta_clicked', {
        location: 'hero',
        type: 'quiz_link'
      });
    }

    if ((window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'secondary_cta_clicked',
        cta_type: 'quiz'
      });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 pb-8 xs:pb-12 px-4 overflow-hidden">
      {/* Mobile/Tablet Switchers - Top Right */}
      <div className="absolute top-4 right-4 z-20 md:hidden">
        <div className="flex items-center gap-1 rounded-lg bg-background/40 backdrop-blur-sm px-2 py-1.5 shadow-sm">
          <FloatingSwitchers />
        </div>
      </div>

      {/* Background Decorative Elements */}
      <Abstract3DShapes density="balanced" />
      <DotPattern />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 xs:gap-12 items-center">
          {/* Left: Content */}
          <div className="text-center lg:text-left animate-fade-in max-w-5xl mx-auto lg:mx-0">
            <h1 className="font-clash text-4xl xs:text-5xl md:text-6xl lg:text-7xl tracking-hero leading-hero mb-6" style={{ fontWeight: 700 }}>
              <span className="bg-gradient-to-r from-brand-teal to-brand-purple bg-clip-text text-transparent" style={{ fontWeight: 700 }}>
                {t("hero.headline").split(" ")[0]}
              </span>
              {" "}
              <span className="text-foreground" style={{ fontWeight: 700 }}>
                {t("hero.headline").split(" ").slice(1).join(" ")}
              </span>
            </h1>
            
            <p className="text-base xs:text-lg md:text-xl text-foreground/80 mb-8 max-w-2xl mx-auto lg:mx-0">
              {t("hero.subheadline")}
            </p>

            <div className="flex flex-col gap-4 items-center lg:items-start mb-4">
              <Button
                onClick={scrollToForm}
                size="lg"
                variant="default"
                className="group"
              >
                {t("hero.cta")}
                <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Button>
              
              {/* Secondary CTA */}
              <button
                onClick={scrollToQuiz}
                className="text-sm font-semibold text-white hover:text-brand-teal transition-colors flex items-center gap-1.5 group"
              >
                {t("hero.secondaryCta")}
                <Zap className="w-4 h-4 transition-transform group-hover:scale-110" />
              </button>
            </div>

            {/* Enhanced micro-copy */}
            <div className="max-w-md mx-auto lg:mx-0">
              <p className="text-xs xs:text-sm text-foreground/70 flex items-center justify-center lg:justify-start gap-2 font-medium">
                <span>{t("hero.ctaMicrocopy", { count: 500 })}</span>
                <span className="text-foreground/40">•</span>
                <span className="text-foreground/60 font-normal">{t("hero.secondary")}</span>
              </p>
            </div>
          </div>

          {/* Right: Abstract 3D Shapes Illustration */}
          <div className="hidden lg:flex items-center justify-center relative">
            <div className="relative w-full max-w-md aspect-square">
              <Abstract3DShapes density="dense" className="relative" />
              
              {/* Central glow effect */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 bg-brand-teal/20 rounded-full blur-3xl animate-glow" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
